import type { UserRole } from '../types';

export const USER_ROLES: UserRole[] = ['member', 'organizer', 'venue', 'partner', 'co-admin', 'admin'];

// Historical role strings that may still be sitting on unmigrated Firestore
// documents: 'user' was the old restricted/waiting-list tier, 'tester' was
// the old full-access tier before it was renamed to 'member'. Both resolve
// to 'member' below — there is no restricted tier to fall back to anymore,
// so any unrecognized or missing role also resolves to 'member', not a
// lesser one.
const LEGACY_FULL_ACCESS_ROLES = new Set(['user', 'tester']);

const ROLE_RANK: Record<UserRole, number> = {
  member: 0,
  organizer: 1,
  venue: 1,
  partner: 1,
  'co-admin': 2,
  admin: 3,
};

export function normalizeUserRole(value: unknown): UserRole {
  if (USER_ROLES.includes(value as UserRole)) return value as UserRole;
  if (typeof value === 'string' && LEGACY_FULL_ACCESS_ROLES.has(value)) return 'member';
  return 'member';
}

export function isMemberOrAbove(role: unknown): boolean {
  return ROLE_RANK[normalizeUserRole(role)] >= ROLE_RANK.member;
}

// Every account is member-or-above by construction (member is rank 0, the
// floor) — this always returns true. Kept as an explicit, named check
// rather than inlining `true` so call sites still read as an intentional
// access decision, and so a future higher-than-member restricted tier (if
// one is ever reintroduced) only needs a rank change here, not call-site
// changes.
export function canAccessApp(role: unknown): boolean {
  return isMemberOrAbove(role);
}

// Vibe at Venues is open to every app user (previously organizer/venue and
// above only). Owner-only actions inside it — editing, followers, analytics,
// calendar imports — are still gated separately and enforced server-side.
export function canAccessVenueVibes(role: unknown): boolean {
  return canAccessApp(role);
}

// Historically gated at "tester or above" — now equivalent to
// canAccessApp() since every member clears that bar, so Aura Vibe (like
// every other former tester-only surface) is open to all standard members.
export function canAccessAuraVibe(role: unknown): boolean {
  return isMemberOrAbove(role);
}

export function isPartner(role: unknown): boolean {
  return normalizeUserRole(role) === 'partner';
}
